import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { CustomerSelectorProps } from "models/propTypes/invoicePropTypes";
import { TranslationKeys } from "translations";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { CustomerFormTypes, InvoiceFormTypes } from "models/formTypes";
import { FieldConstants, FormFieldConstants, IconDefinitionConstants, InvoiceConstants } from "utils/constants";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { SearchInputProps } from "models/propTypes/wintFormPropsTypes";
import { CommonEnums, StyleEnums } from "models/enums";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { MessageBubbleDetails } from "models/propTypes/messagePropTypes";
import { NestedKeyOf } from "models/utilTypes/nestedKeyType";
//#endregion
//#region components
import { HorizontalFormField, SearchInput, Suggestions, WizardSection } from "components/common/wintForms";
import {
  useCreateCustomerDialog,
  useCustomerSearch,
  useReferencePersonDialog,
  useReferencePersonSearch,
} from "components/customer";
import { useAPIRequestKeyRef, useFloatingMessages } from "components/common";
import { useInvoiceDefaultDeliverySettings } from "components/invoice";
import { useLocation, useNavigate } from "react-router-dom";
import { useRatingService, useInvoiceService, useCustomerService } from "serviceHooks";
import useLoadSendMailOptions from "../sendMailOptions/useSendMailOptions";
import useSetMailOptionsDefaults from "../sendMailOptions/useSetMailOptionsDefaults";
import { useGetEmailValues } from "./useGetEmailValues";
import ReferenceCodeCaption from "./referenceCodeCaption";
import { toReferencePerson } from "components/customer/forms/referencePersonMapping";
//#endregion
import {
  DateHelper,
  InvoiceHelper,
  LocalizationHelperInstance,
  PersonNumberHelper,
  ServiceHelper,
} from "utils/helpers";
import { Box } from "@mui/material";

const CustomerSelectionStep: React.FC<CustomerSelectorProps> = (props) => {
  const ratingService = useRatingService();
  const invoiceService = useInvoiceService();
  const customerService = useCustomerService();
  const {
    wizardProps: wizardStepProps,
    isQuotation,
    disableCustomerSelection,
    connect,
    values,
    submitted,
    setValues,
  } = props;

  // loadCustomerInfo's async callbacks close over `values` from the render that triggered the
  // customer fetch. If the user edits a row (e.g. picks a new, lock-eligible article) while that
  // fetch is still in flight, `values.products` in those callbacks is stale by the time they run.
  // This ref always holds the latest products, read instead of the closure at the one call site
  // (the VAT-reset decision) where stale data would silently leave a locked row's VAT un-reset.
  const latestProductsRef = React.useRef(values.products);
  latestProductsRef.current = values.products;

  const [topCustomers, setTopCustomers] = React.useState<AutocompleteListItem[]>([]);
  const [topReferencePersons, setTopReferencePersons] = React.useState<AutocompleteListItem[]>([]);
  // undefined = the customer's reference persons haven't been loaded yet. Distinct from [] (loaded, none),
  // because "not loaded yet" must not be rendered as "this person has no reference code".
  const [customerReferencePersons, setCustomerReferencePersons] = React.useState<WintSuperkollAPIModel.Reference[]>();
  const { searchReferencePersonsWithCustomer } = useReferencePersonSearch();
  const searchCustomers = useCustomerSearch();
  const { enqueueMessage } = useFloatingMessages();
  const noVatCompany = useSelector((store: AppState) => store.auth.selectedCompany.noVat);

  const customerSearchProps = connect("customer");
  const referencePersonSearchProps = connect("referencePerson");

  const defaultPaymentTerms = useSelector((store: AppState) => store.auth.selectedCompany.defaultInvoicePaymentTerms);
  const pageroEInvoice = useSelector((store: AppState) => store.auth.selectedCompany.pageroEInvoice);

  const navigate = useNavigate();
  const location = useLocation();
  const { getUpdatedDefaultDeliverySettings } = useInvoiceDefaultDeliverySettings();

  //Read customer id from route state and set it to form
  React.useEffect(() => {
    const state: any = location.state;

    if (state && typeof state === "object") {
      if (state.customerId) {
        onCustomerSelect({ id: state.customerId, value: state.customerName });

        if (state.customerId || state.productId) {
          delete state.customerId;
          delete state.customerName;

          navigate(location, { replace: true });
        }
      }
    }
  }, [location.state]);

  const setCustomerLoading = (id: number | null) => {
    connect(FormFieldConstants.Invoice.Create.LoadingCustomer).setValue(id);
  };

  const { apiRequestKeyRef: customerApiKeyRef, updateKey } = useAPIRequestKeyRef();

  React.useEffect(() => {
    customerService.getCustomerSuggestions().then((res: WintSuperkollAPIModel.CustomerSearchResponse[]) => {
      if (res && res.length > 0) {
        setTopCustomers(
          res.map(
            (item): AutocompleteListItem => ({
              id: item.id ?? 0,
              value: item.name ?? "",
              icon: IconDefinitionConstants.WCompany,
              group: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
            })
          )
        );
      } else if (topCustomers.length !== 0) setTopCustomers([]);
    });
  }, []);

  const setReferencePersonSuggestions = (customerId?: number, apiKey?: any) => {
    if (customerId) {
      ServiceHelper.withAPIRequestKey<WintSuperkollAPIModel.ReferenceSearchResponse[]>(
        invoiceService.getReferencePersonSuggestions(customerId),
        apiKey
      ).then((referenceResult) => {
        if (customerApiKeyRef.current === referenceResult.key) {
          const topReferencePersons = referenceResult.data
            ? (referenceResult.data
                .filter((reference) => reference.name)
                .map(
                  (reference): AutocompleteListItem => ({
                    id: reference.id ?? 0,
                    value: reference.name ?? "",
                    description: pageroEInvoice ? reference.referenceIdentifier : undefined,
                    data: reference,
                    icon: IconDefinitionConstants.WUser,
                  })
                ) ?? [])
            : [];

          setTopReferencePersons(topReferencePersons);
        }
      });
    } else {
      setTopReferencePersons([]);
    }
  };

  const updatePenaltyInterestRate = (
    customerId: number,
    type: WintSuperkollAPIModel.InvoicePenaltyInterestType,
    apiKey?: any
  ) => {
    ServiceHelper.withAPIRequestKey(customerService.getPenaltyInterestRate(customerId, type), apiKey).then((result) => {
      if (customerApiKeyRef.current === result.key) {
        const rate = result.data;
        const penaltyInterestRateFieldName: NestedKeyOf<InvoiceFormTypes.Create> =
          "otherSettings.penaltyInterestPercentage";
        connect(penaltyInterestRateFieldName).setValue(rate != null ? rate * 100 : null);
      }
    });
  };

  const { loadSendMailOptions } = useLoadSendMailOptions();
  const { getUpdatedDefaultEmailOptions } = useSetMailOptionsDefaults(connect, values);
  const getEmailValues = useGetEmailValues();

  const loadCustomerInfo = (id?: number) => {
    setCustomerLoading(id ?? null);
    const currentKey = updateKey();

    if (id) {
      setReferencePersonSuggestions(id as number, currentKey);

      ServiceHelper.withAPIRequestKey(customerService.getCustomerRating(id), currentKey).then((result) => {
        if (customerApiKeyRef.current === result.key) {
          connect(FormFieldConstants.Invoice.Create.CustomerRating).setValue(result.data);
        }
      });

      if (!isQuotation) {
        ServiceHelper.withAPIRequestKey(customerService.getPenaltyInterestType(id), currentKey).then((result) => {
          if (customerApiKeyRef.current === result.key) {
            const penaltyInterestTypeFieldName: NestedKeyOf<InvoiceFormTypes.Create> =
              "otherSettings.penaltyInterestType";
            connect(penaltyInterestTypeFieldName).setValue(result.data);
          }
          updatePenaltyInterestRate(id, result.data, currentKey);
        });
      }

      Promise.all([
        ServiceHelper.withAPIRequestKey<WintSuperkollAPIModel.Customer>(customerService.getCustomer(id), currentKey),
        loadSendMailOptions(values.id, id),
      ])
        .then(([result, sendMailOptions]) => {
          const customerResult = result.data;
          if (customerApiKeyRef.current === result.key && customerResult) {
            const defaultReferencePerson = customerResult.referencePersonsList?.find(
              (refPerson) => refPerson.isDefault
            );
            const paymentTerms = customerResult.paymentTerms ?? defaultPaymentTerms;

            const customerPersonNumberLong =
              customerResult.orgNumber && PersonNumberHelper.normalize(customerResult.orgNumber, true);
            const tenantsOrgNumber =
              customerResult.tenantsOwnerAssociationOrgNumber &&
              PersonNumberHelper.normalize(customerResult.tenantsOwnerAssociationOrgNumber, false);

            const { mailOptions, mailOptionsAuto, defaultEmailToSettings, defaultReminderEmailToSettings } =
              getEmailValues(customerResult, defaultReferencePerson?.emailAddress, sendMailOptions);

            // Domestic vs. foreign, for the invoice-row VAT lock. isEUCustomer can't be used for
            // this: it's false for both Swedish AND non-EU foreign customers (e.g. a US customer),
            // so it can't tell "domestic" apart from "foreign but not EU". The rule itself (billing
            // address only, mirroring Core's IsToForeignCountry) lives in InvoiceHelper.
            const isDomesticCustomer = InvoiceHelper.isDomesticCountryCode(customerResult.billingAddress?.countryCode);

            // Computed before setValues, not inside it: a state updater must be pure. React can
            // re-run it (and deliberately does, twice, under StrictMode in dev), which would set
            // this flag twice and show the notice twice. The updater below only applies the
            // decision made here. Reads latestProductsRef rather than `values.products`: this
            // callback closes over the render that started the customer fetch, and a row edited
            // (e.g. a new, lock-eligible article picked) while that fetch is in flight must still
            // be included.
            const rowsNeedingVatReset = InvoiceHelper.getRowsNeedingVatReset(
              latestProductsRef.current,
              isDomesticCustomer,
              props.revenueAccountsEnabled,
              noVatCompany,
              props.creditedInvoiceId
            );
            const vatWasResetForCustomerChange = rowsNeedingVatReset.size > 0;

            setValues((prevState) => {
              const { otherSettings: otherSettingsUpdated, recurringSettings: recurringSettingsUpdated } =
                getUpdatedDefaultDeliverySettings(customerResult, prevState);
              return {
                ...prevState,
                customer: { id: customerResult.id, value: customerResult.name ?? "" },
                customerEmail: customerResult.emailAddress,
                referencePerson: defaultReferencePerson
                  ? { id: defaultReferencePerson?.id, value: defaultReferencePerson?.name ?? "" }
                  : undefined,
                referencePersonEmail: defaultReferencePerson?.emailAddress,
                otherSettings: {
                  ...otherSettingsUpdated,
                  languageKey: customerResult.language ?? InvoiceConstants.DefaultInvoiceLanguageCode,
                  showCurrencyCode: customerResult.showCurrencyCode,
                  paymentTerms: paymentTerms,
                  dueDate: isQuotation
                    ? prevState.otherSettings.dueDate
                    : DateHelper.addDays(prevState.otherSettings.invoiceDate, paymentTerms),
                  automaticReminderSendMail: getUpdatedDefaultEmailOptions(
                    otherSettingsUpdated?.automaticReminderSendMail ?? {},
                    mailOptionsAuto,
                    defaultReminderEmailToSettings
                  ),
                  defaultReminderEmailToSettings: defaultReminderEmailToSettings,
                },
                isEUCustomer: customerResult.isEUInvoiceCustomer,
                isDomesticCustomer: isDomesticCustomer,
                products: (prevState.isEUCustomer !== customerResult.isEUInvoiceCustomer
                  ? prevState.products?.map((p) => ({ ...p, isEUCustomer: customerResult.isEUInvoiceCustomer }))
                  : prevState.products
                )?.map((p) => {
                  // Customer selected/changed after rows already exist: a locked row (its article
                  // has a configured account) whose VAT no longer matches that article's VAT - e.g.
                  // it was edited while unlocked for a previous, foreign customer - snaps back to
                  // the article's VAT, with a notice so the change isn't silent. Which rows those
                  // are was decided above, before this updater ran.
                  if (rowsNeedingVatReset.has(p.uniqueId)) {
                    // isRowVatLocked already guaranteed articleVat is defined for these rows.
                    return { ...p, tax: p.articleVat! };
                  }
                  return p;
                }),
                rutRot: {
                  ...(prevState.rutRot ?? {}),
                  rutRotPersonNumber: customerPersonNumberLong?.replace("-", ""),
                  propertyDesignation: customerResult.propertyDesignation,
                  tenantsOrgNumber: tenantsOrgNumber?.replace("-", ""),
                  apartmentNumber: customerResult.apartmentNumber,
                },
                recurringSettings: {
                  ...recurringSettingsUpdated,
                  recurringSendMail: getUpdatedDefaultEmailOptions(
                    recurringSettingsUpdated?.recurringSendMail ?? {},
                    mailOptionsAuto,
                    defaultEmailToSettings
                  ),
                },
                mailOptions: mailOptions,
                previewSendMail: getUpdatedDefaultEmailOptions({}, mailOptions, defaultEmailToSettings),
                defaultSendType: customerResult.invoiceDeliverySettings?.defaultInvoiceSendOption,
                defaultEmailToSettings: defaultEmailToSettings,
              };
            });

            setCustomerReferencePersons(customerResult.referencePersonsList ?? []);

            if (vatWasResetForCustomerChange) {
              enqueueMessage(
                LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.VatResetNotice),
                StyleEnums.DisplayType.Info,
                true
              );
            }

            setCustomerLoading(null);
          } else if (customerApiKeyRef.current === result.key) {
            // The request finished but carried no customer. Clear the spinner anyway — otherwise the
            // reference-person field stays disabled/loading for good and the step can't be completed.
            setCustomerLoading(null);
          }
        })
        .catch(() => {
          setCustomerLoading(null);
        });
    }
  };

  React.useEffect(() => {
    if (!values.customer?.id) return;
    const currentKey = updateKey();
    setReferencePersonSuggestions(values.customer.id as number, currentKey);
    ServiceHelper.withAPIRequestKey<WintSuperkollAPIModel.Customer>(
      customerService.getCustomer(values.customer.id as number),
      currentKey
    )
      .then((result) => {
        if (customerApiKeyRef.current === result.key) {
          setCustomerReferencePersons(result.data?.referencePersonsList ?? []);
        }
      })
      .catch(() => undefined);
  }, []);

  const onCustomerSelect = (customer: AutocompleteListItem | null) => {
    customerSearchProps.setValue(customer, true);
    referencePersonSearchProps.setValue(undefined);
    setCustomerReferencePersons(undefined);
    loadCustomerInfo(customer?.id as number);
  };

  const upsertCustomerReferencePerson = (formValues: CustomerFormTypes.ReferencePerson) => {
    const person = toReferencePerson(formValues);

    setCustomerReferencePersons((prev) => {
      if (!prev) return [person];
      const index = prev.findIndex((p) => p.id === person.id);
      if (index === -1) return [...prev, person];
      const updated = [...prev];
      updated[index] = person;
      return updated;
    });
  };

  const { showPreviewDialog } = useCreateCustomerDialog();

  const onCustomerAdd = (name?: string): Promise<AutocompleteListItem | undefined> => {
    return new Promise((resolve) => {
      const onCustomerCreateSuccess = (customerId?: number, customerName?: string) => {
        const customerResult = { id: customerId, value: customerName ?? "" };
        onCustomerSelect(customerResult);
        resolve(customerResult);
      };

      const onPreviewDialogClosed = () => {
        resolve(undefined);
      };

      showPreviewDialog(name, onCustomerCreateSuccess, onPreviewDialogClosed);
    });
  };

  // One dialog for both cases (shared with previewSendOptionSection.tsx via useReferencePersonDialog):
  // a resolved person is edited, no person means add — optionally pre-filled with a name typed into
  // the freeSolo field.
  const { showReferencePersonDialog } = useReferencePersonDialog();

  const openReferencePersonDialog = (person?: WintSuperkollAPIModel.Reference, newName?: string) =>
    showReferencePersonDialog({
      customerId: values.customer?.id as number,
      person,
      newName,
      onSaved: (submittedValues) => {
        upsertCustomerReferencePerson(submittedValues);

        // Re-selecting runs updateDefaultEmailToOptions, which rewrites mailOptions/defaultEmailTo and
        // would discard a recipient the user picked by hand. Only an add — or a switch to a different
        // person — needs that; editing the person who is already selected does not.
        if (submittedValues.id !== (values.referencePerson?.id as number | undefined)) {
          onReferencePersonSelect({
            id: submittedValues.id,
            value: submittedValues.name,
            data: { mail: submittedValues.email },
          });
          return;
        }

        // The name may have changed, so the field label still has to follow — just without the
        // mail-defaults rewrite above.
        referencePersonSearchProps.setValue(
          { id: submittedValues.id, value: submittedValues.name, data: { mail: submittedValues.email } },
          true
        );
      },
    });

  const onAddReferencePerson = (name?: string) => openReferencePersonDialog(undefined, name);

  const getCustomMessageForCustomer = () => {
    if (values.customerRating) {
      const ratingInfo = ratingService.getRatingInfo(CommonEnums.OrganizationTypes.Customer, values.customerRating);
      const message: MessageBubbleDetails = {
        type: ratingInfo.displayType,
        message: `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.CreditRating)} ${
          ratingInfo.ratingText
        } ${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.OutOf, "", {
          total: "100",
        })}. ${LocalizationHelperInstance.getFormattedMessage(ratingInfo.displayText)}`,
      };
      return message;
    } else return undefined;
  };

  const { updateDefaultEmailToOptions } = useSetMailOptionsDefaults(connect, values);

  const onReferencePersonSelect = (referencePerson: AutocompleteListItem | null) => {
    updateDefaultEmailToOptions(referencePerson);

    referencePersonSearchProps.setValue(referencePerson, true);
  };

  // The reference-person field is freeSolo: typing sets a value with no id, so an id-less value is a
  // name the user typed, not a saved reference person — it has no code and can only be *added*.
  const selectedReferencePersonId = values.referencePerson?.id as number | undefined;
  // Emptying the field by hand doesn't clear the value: freeSolo writes { value: "" } on every
  // keystroke (only the clear button writes undefined), so an all-empty value means nothing is
  // selected — not a reference person whose code happens to be missing.
  const hasReferencePerson = !!(selectedReferencePersonId || values.referencePerson?.value);
  const selectedReferencePerson = selectedReferencePersonId
    ? customerReferencePersons?.find((person) => person.id === selectedReferencePersonId)
    : undefined;
  const referenceCode = selectedReferencePerson?.referenceIdentifier;
  // A selected id we can't resolve in the customer's list (not loaded yet, or missing from it) means
  // we don't know the person's real email/phone/isDefault. The dialog saves the whole person, so
  // editing from a partial copy would blank those fields — say nothing about the code rather than
  // offer a destructive shortcut.
  const canShowReferenceCode =
    customerReferencePersons !== undefined && (!selectedReferencePersonId || !!selectedReferencePerson);

  const onReferenceCodeLinkClick = () =>
    openReferencePersonDialog(selectedReferencePerson, values.referencePerson?.value);

  return (
    <WizardSection
      header={TranslationKeys.Invoice.Customer}
      description={values.customer?.value ?? TranslationKeys.Invoice.ReceiverDescription}
      submitted={submitted}
      hasErrors={customerSearchProps.error || referencePersonSearchProps.error}
      wizardProps={wizardStepProps}
      testId="customerSelectionStep"
    >
      <HorizontalFormField<SearchInputProps>
        Component={SearchInput}
        stackFieldContent
        {...customerSearchProps}
        setValue={onCustomerSelect}
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Customer)}
        descriptionText={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SearchCustomerOrCreate)}
        suggestedItems={topCustomers}
        items={searchCustomers}
        customMessage={getCustomMessageForCustomer()}
        messageBubblePlacement="top"
        disabled={disableCustomerSelection}
        actions={[
          {
            id: InvoiceConstants.AddCustomerAction,
            value: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Invoice.SaveValueAsCustomer,
              undefined,
              { key: FieldConstants.ValueTemplateText }
            ),
            icon: IconDefinitionConstants.WPlus,
            onActionClicked: onCustomerAdd,
            actionSuccessMessage: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Saved),
            hide: (options: AutocompleteListItem[], value?: string, isValueFromOptions?: boolean) =>
              !value || isValueFromOptions || options.some((op) => op.value === value),
          },
        ]}
      >
        <Box sx={{ pt: 2 }}>
          <Suggestions
            setValue={onCustomerSelect}
            setTouched={customerSearchProps.setTouched}
            suggestions={topCustomers as SelectionItem[]}
            onAddButtonClick={onCustomerAdd}
            addButtonLabel={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.NewCustomer)}
          />
        </Box>
      </HorizontalFormField>
      {values.customer ? (
        <>
          <HorizontalFormField<SearchInputProps>
            freeSolo
            Component={SearchInput}
            stackFieldContent
            {...referencePersonSearchProps}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ReferencePerson)}
            descriptionText={
              <Box sx={{ mt: 0.5 }}>
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SearchReferencePersons)}
              </Box>
            }
            fieldValueDynamicDescriptionText={
              pageroEInvoice && hasReferencePerson && canShowReferenceCode ? (
                <ReferenceCodeCaption
                  code={referenceCode}
                  hasPerson={!!selectedReferencePerson}
                  onLinkClick={onReferenceCodeLinkClick}
                />
              ) : undefined
            }
            suggestedItems={topReferencePersons}
            items={searchReferencePersonsWithCustomer.bind(null, values.customer?.id as number)}
            optional
            isLoading={values.loadingCustomer !== null}
            setValue={onReferencePersonSelect}
          >
            {values.loadingCustomer === null && (
              <Box sx={{ pt: 2 }}>
                <Suggestions
                  setValue={onReferencePersonSelect}
                  setTouched={referencePersonSearchProps.setTouched}
                  suggestions={topReferencePersons as SelectionItem[]}
                  onAddButtonClick={onAddReferencePerson}
                  addButtonLabel={LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.Invoice.NewReferencePerson
                  )}
                />
              </Box>
            )}
          </HorizontalFormField>
        </>
      ) : (
        <></>
      )}
    </WizardSection>
  );
};

export default CustomerSelectionStep;
